import type { AccountInfo } from '@azure/msal-browser';

export type AccountIdentifiers = Partial<
    Pick<
        AccountInfo,
        'homeAccountId' | 'localAccountId' | 'username' | 'idTokenClaims'
    >
>;

/**
 * Check if tho account objects represent the same account.
 *
 * @param lhs The first account.
 * @param rhs The second account.
 */
export function accountsAreEqual(
    lhs: AccountIdentifiers,
    rhs: AccountIdentifiers
): boolean {
    return (
        lhs.homeAccountId === rhs.homeAccountId &&
        lhs.localAccountId === rhs.localAccountId &&
        lhs.username === rhs.username &&
        lhs.idTokenClaims?.team === rhs.idTokenClaims?.team
    );
}

/**
 * Same as `accountsAreEqual`, but it also allows `null` values to be equal.
 *
 * @see accountsAreEqual
 *
 * @param lhs The first account.
 * @param rhs The second account.
 */
export function accountsAreEqualWithNull(
    lhs: AccountIdentifiers | null,
    rhs: AccountIdentifiers | null
): boolean {
    if (lhs === null && rhs === null) return true;
    if (lhs !== null && rhs !== null) return accountsAreEqual(lhs, rhs);
    return false;
}

/**
 * Checks if two arrays represent the same accounts.
 *
 * @param arrayA
 * @param arrayB
 */
export function accountArraysAreEqual(
    arrayA: Array<AccountIdentifiers>,
    arrayB: Array<AccountIdentifiers>
): boolean {
    if (arrayA.length !== arrayB.length) return false;

    const comparisonArray = [...arrayB];

    return arrayA.every((elementA) => {
        const elementB = comparisonArray.shift();
        if (!elementA || !elementB) return false;
        return accountsAreEqual(elementA, elementB);
    });
}
